import React from "react";
import {
	Column,
	compareNumbersAsc,
	compareObjects,
	daysOfWeek,
	Heading,
	Modal,
	ModalProps,
	Row,
	Section,
	sort,
} from "@hex-insights/core";
import { Employee, Guardian, MessageAvailabilityPeriod, User, UserNameAndImageFragment } from "../../../../Utilities";
import { MessageAvailabilityPeriodFormat, UserFormat } from "../../../Format";
import { MessageAvailabilityPeriodUtilities } from "../../../Utilities";
import * as UserComponents from "../../User";
import styles from "./styles.module.css";

export type UserMessageAvailabilityModalProps = {
	user: Pick<User, "id"> &
		UserNameAndImageFragment & {
			messageAvailabilityPeriods: Pick<
				MessageAvailabilityPeriod,
				"id" | "dayOfWeek" | "startTimeMinutes" | "endTimeMinutes"
			>[];
			person: {
				employee: Pick<Employee, "jobTitle"> | null;
				guardian: Pick<Guardian, "id"> | null;
			} | null;
		};
} & Pick<ModalProps, "ifRef" | "onClose">;

export function UserMessageAvailabilityModal({ user, ifRef, onClose }: UserMessageAvailabilityModalProps) {
	const { todaysAvailability } = React.useMemo(() => {
		const todaysAvailability = MessageAvailabilityPeriodUtilities.getUserTodayAvailability(
			user.messageAvailabilityPeriods,
		);
		return { todaysAvailability };
	}, [user.messageAvailabilityPeriods]);

	const schedule = React.useMemo(() => {
		const schedule: UserMessageAvailabilityModalProps["user"]["messageAvailabilityPeriods"][] = [
			[],
			[],
			[],
			[],
			[],
			[],
			[],
		];
		for (let i = 0; i < user.messageAvailabilityPeriods.length; i++) {
			const period = user.messageAvailabilityPeriods[i];
			schedule[period.dayOfWeek].push(period);
		}
		const compare = compareObjects("startTimeMinutes", compareNumbersAsc());
		for (let i = 0; i < schedule.length; i++) {
			schedule[i].sort(compare);
		}
		return schedule;
	}, [user.messageAvailabilityPeriods]);

	return (
		<Modal ifRef={ifRef} onClose={onClose}>
			<Modal.Body>
				<Column justify="spaced-start">
					<Row justify="spaced-start" align="center">
						<UserComponents.UserWithMessagingStatusIcon user={user} size={46} />

						<Column justify="spaced-start" verticalSpacing="0.125rem">
							<span className={styles["user-modal__user__name"]}>{UserFormat.name(user)}</span>
							<span className={styles["user-modal__user__description"]}>{UserFormat.role(user)}</span>
							<span
								className={
									styles["user-modal__user__description"] + " " + styles["user-modal__user__description--availability"]
								}
							>
								{todaysAvailability}
							</span>
						</Column>
					</Row>

					<Section>
						<Section.Header>
							<Heading.H5 noMargin>Availability Hours</Heading.H5>
						</Section.Header>
						<Section.Body style={{ padding: "0.5rem 0.75rem" }}>
							<Column justify="spaced-start" verticalSpacing="0.25rem">
								{schedule.map((periods, dayOfWeek) => (
									<span key={dayOfWeek} style={{ fontSize: "0.875rem" }}>
										<span key={dayOfWeek} style={{ fontWeight: "bold" }}>
											{daysOfWeek[dayOfWeek]}
										</span>
										:{" "}
										<span style={{ color: "var(--neutral-colors--secondary)" }}>{formatDayAvailability(periods)}</span>
									</span>
								))}
							</Column>
						</Section.Body>
					</Section>
				</Column>
			</Modal.Body>
		</Modal>
	);
}

function formatDayAvailability(
	messageAvailabilityPeriods: Pick<MessageAvailabilityPeriod, "startTimeMinutes" | "endTimeMinutes">[],
) {
	if (messageAvailabilityPeriods.length === 0) {
		return "Not Available";
	}

	return sort(messageAvailabilityPeriods, compareObjects("startTimeMinutes", compareNumbersAsc()))
		.map(
			(period) =>
				`${MessageAvailabilityPeriodFormat.Fields.startTimeMinutes(
					period.startTimeMinutes,
				)} - ${MessageAvailabilityPeriodFormat.Fields.endTimeMinutes(period.endTimeMinutes)}`,
		)
		.join(", ");
}
